import React, { useState, useMemo, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, Typography, IconButton, Box, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import { printDailyShiftUtils } from "./Printing/printDailyShiftUtils"; // تأكد من مسار الملف/التصدير

// إضافة خريطة ترجمة لأنواع العمليات
const typeLabels = {
  invoice: "فاتورة",
  expenses: "مصروفات",
  withdrawal: "سحب من الخزنة",
  deposit: "ايداع الي الخزنة",
};

const ShiftTransactionsDialog = ({ open, onClose, transactions = [], loading = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // اختصار لوحة المفاتيح Ctrl/Cmd + P لطباعة الشيفت
  useEffect(() => {
    const handler = (e) => {
      const isPrintShortcut = (e.ctrlKey || e.metaKey) && (e.key === "p" || e.key === "P");
      if (!isPrintShortcut) return;
      // فقط عندما يكون الديالوج مفتوحاً
      if (!open) return;
      e.preventDefault();
      handlePrintAll();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]); // handlePrintAll used from closure; ok as recreated

  // حساب المجاميع
  const { totalWithdrawalsExpenses, totalInvoices, totalDeposits, totalNet } = useMemo(() => {
    let withdrawalsExpenses = 0;
    let invoices = 0;
    let deposits = 0;
    (transactions || []).forEach(tx => {
      const amt = parseFloat(tx.amount) || 0;
      if (tx.type === "withdrawal" || tx.type === "expenses") withdrawalsExpenses += amt;
      if (tx.type === "invoice") invoices += amt;
      if (tx.type === "deposit") deposits += amt;
    });
    const totalNet = invoices + deposits - withdrawalsExpenses;
    return {
      totalWithdrawalsExpenses: withdrawalsExpenses,
      totalInvoices: invoices,
      totalDeposits: deposits,
      totalNet,
    };
  }, [transactions]);
  
  const openDetail = (tx) => {
    setSelectedTransaction(tx);
    setDetailOpen(true);
  };
  const closeDetail = () => {
    setSelectedTransaction(null);
    setDetailOpen(false);
  };

  // طباعة كل بيانات الشيفت مع المجاميع
  const handlePrintAll = async () => {
    // بناء HTML للجدول كما يظهر في الواجهة
    const headerCols = [
      "م",
      "رقم العملية",
      "النوع",
      "المبلغ",
      "الخزنة",
      "العميل",
      "نوع الأوردر",
      "تاريخ العملية"
    ];

    const rowsHtml = (transactions || []).map((tx, idx) => {
      return `<tr>
        <td style="padding:6px;border:1px solid #ddd;text-align:right">${idx + 1}</td>
        <td style="padding:6px;border:1px solid #ddd;text-align:right">${tx.id}</td>
        <td style="padding:6px;border:1px solid #ddd;text-align:right">${typeLabels[tx.type] || tx.type}</td>
        <td style="padding:6px;border:1px solid #ddd;text-align:right">${(parseFloat(tx.amount)||0).toLocaleString()}</td>
        <td style="padding:6px;border:1px solid #ddd;text-align:right">${tx.bank?.name || "-"}</td>
        <td style="padding:6px;border:1px solid #ddd;text-align:right">${tx.invoice?.customer?.name || "-"}</td>
        <td style="padding:6px;border:1px solid #ddd;text-align:right">${tx.invoice?.order?.order_type || "-"}</td>
        <td style="padding:6px;border:1px solid #ddd;text-align:right">${tx.transaction_date || "-"}</td>
      </tr>`;
    }).join("");

    const tableHtml = `
      <table style="width:100%;border-collapse:collapse;direction:rtl">
        <thead>
          <tr>${headerCols.map(c => `<th style="border:1px solid #ddd;padding:6px;text-align:right">${c}</th>`).join("")}</tr>
        </thead>
        <tbody>
          ${rowsHtml || `<tr><td colspan="${headerCols.length}" style="padding:6px;text-align:center">لا توجد عمليات</td></tr>`}
        </tbody>
      </table>
    `;

    const payload = {
      transactions: transactions || [],
      totals: {
        totalWithdrawalsExpenses,
        totalInvoices,
        totalDeposits,
        totalNet,
      },
      tableHtml, // HTML جاهز للطباعة
    };

    try {
      await printDailyShiftUtils(payload);
    } catch (err) {
      console.error("❌ خطأ في طباعة بيانات الشيفت:", err);
      alert("فشل في الطباعة.");
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "96vw", sm: "92vw", md: "1200px", lg: "1400px" },
            height: { xs: "80vh", sm: "84vh", md: "88vh", lg: "90vh" },
            maxHeight: "98vh",
            m: 0,
            borderRadius: 2,
            bgcolor: isDark ? "#12121b" : "#fff",
            color: isDark ? "#fff" : "#232334",
          },
        }}
        BackdropProps={{ sx: { backdropFilter: "blur(2px)" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
          <Typography>معاملات الشيفت</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePrintAll}
              startIcon={<PrintIcon />}
              sx={{ fontWeight: 700, textTransform: "none" }}
              aria-label="طباعة الشيفت (Ctrl+P)"
            >
              طباعة (Ctrl+P)
            </Button>
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflowY: "auto", height: "calc(100% - 112px)", p: 2 }}>
          {/* مجاميع سريعة أعلى الجدول */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Box sx={{
              p: 2,
              borderRadius: 1,
              minWidth: 220,
              bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff4f4",
              border: 1,
              borderColor: isDark ? "divider" : "transparent"
            }}>
              <Typography sx={{ fontWeight: 700, color: isDark ? "#ffb4b4" : "#b71c1c" }}>المسحوبات + المصروفات</Typography>
              <Typography sx={{ fontSize: 20 }}>{totalWithdrawalsExpenses.toLocaleString()} ج.م</Typography>
            </Box>
            <Box sx={{
              p: 2,
              borderRadius: 1,
              minWidth: 220,
              bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#eaf6ff",
              border: 1,
              borderColor: isDark ? "divider" : "transparent"
            }}>
              <Typography sx={{ fontWeight: 700, color: isDark ? "#90caf9" : "#1565c0" }}>إجمالي الفواتير</Typography>
              <Typography sx={{ fontSize: 20 }}>{totalInvoices.toLocaleString()} ج.م</Typography>
            </Box>
            <Box sx={{
              p: 2,
              borderRadius: 1,
              minWidth: 220,
              bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ecfff3",
              border: 1,
              borderColor: isDark ? "divider" : "transparent"
            }}>
              <Typography sx={{ fontWeight: 700, color: isDark ? "#a5d6a7" : "#2e7d32" }}>إيداع إلى الخزنة</Typography>
              <Typography sx={{ fontSize: 20 }}>{totalDeposits.toLocaleString()} ج.م</Typography>
            </Box>
            <Box sx={{
              p: 2,
              borderRadius: 1,
              minWidth: 220,
              bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#fff8e1",
              border: 1,
              borderColor: isDark ? "divider" : "transparent"
            }}>
              <Typography sx={{ fontWeight: 700, color: isDark ? "#ffe082" : "#ff8f00" }}>الصافي</Typography>
              <Typography sx={{ fontSize: 20 }}>{(totalNet || 0).toLocaleString()} ج.م</Typography>
            </Box>
          </Box>

          {loading ? (
            <CircularProgress />
          ) : !transactions || transactions.length === 0 ? (
            <Typography color="text.secondary">لا توجد معاملات لهذا الشيفت.</Typography>
          ) : (
            <Table sx={{ bgcolor: isDark ? "transparent" : undefined }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 60 }}>م</TableCell>
                  <TableCell>رقم العملية</TableCell>
                  <TableCell>النوع</TableCell>
                  <TableCell>المبلغ</TableCell>
                  <TableCell>الخزنة</TableCell>
                  <TableCell>العميل</TableCell>
                  <TableCell>نوع الأوردر</TableCell>
                  <TableCell>التاريخ</TableCell>
                  <TableCell>تفاصيل</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx, idx) => (
                  <TableRow key={tx.id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{tx.id}</TableCell>
                    <TableCell>{typeLabels[tx.type] || tx.type}</TableCell>
                    <TableCell>{tx.amount} ج.م</TableCell>
                    <TableCell>{tx.bank?.name || "غير محدد"}</TableCell>
                    <TableCell>{tx.invoice?.customer?.name || "بدون عميل"}</TableCell>
                    <TableCell>{tx.invoice?.order?.order_type || "-"}</TableCell>
                    <TableCell>{tx.transaction_date}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => openDetail(tx)}>
                        عرض
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* مودال تفاصيل المعاملة */}
      <TransactionDetailDialog open={detailOpen} onClose={closeDetail} tx={selectedTransaction} isDark={isDark} />
    </>
  );
};

// موديال عرض تفاصيل العملية (داخل نفس الملف)
const TransactionDetailDialog = ({ open, onClose, tx, isDark }) => {
  if (!tx) return null;

  const formatTime12 = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true });
    }
    // fallback for time-only strings like "14:30:00"
    const parts = String(val).split(":");
    if (parts.length >= 2 && !isNaN(parts[0])) {
      let hh = parseInt(parts[0], 10);
      const mm = parts[1];
      const ampm = hh >= 12 ? "PM" : "AM";
      hh = hh % 12 || 12;
      return `${hh}:${mm} ${ampm}`;
    }
    return val;
  };

  const timeStr = formatTime12(tx.created_at || tx.transaction_date);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { maxHeight: "90vh", bgcolor: isDark ? "#12121b" : "#fff", color: isDark ? "#fff" : "#232334" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography>تفاصيل العملية #{tx.id} — {timeStr}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowY: "auto" }}>
        <Box component="section" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">معلومات أساسية</Typography>
          <Typography>النوع: {typeLabels[tx.type] || tx.type}</Typography>
          <Typography>المبلغ: {tx.amount}</Typography>
          <Typography>تاريخ العملية: {tx.transaction_date}</Typography>
          <Typography>مُنشأ: {tx.created_at}</Typography>
          <Typography>مُحدّث: {tx.updated_at}</Typography>
        </Box>

        <Box component="section" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">المستخدم</Typography>
          <Typography>اسم: {tx.user?.name || "-"}</Typography>
          <Typography>اسم الدخول: {tx.user?.username || "-"}</Typography>
          <Typography>النوع: {tx.user?.type || "-"}</Typography>
        </Box>

        <Box component="section" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">الخزنة</Typography>
          <Typography>الخزنة: {tx.bank?.name || "-"}</Typography>
        </Box>

        {tx.invoice && (
          <Box component="section" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">فاتورة #{tx.invoice.id} — المبلغ: {tx.invoice.total_amount}</Typography>
            <Typography>التاريخ: {tx.invoice.date}</Typography>
            <Typography>الضريبة: {tx.invoice.tax}</Typography>
            {tx.invoice.order && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2">الطلب المرتبط #{tx.invoice.order.id}</Typography>
                <Typography>نوع الطلب: {tx.invoice.order.order_type}</Typography>
                <Typography>حالة الطلب: {tx.invoice.order.status}</Typography>
                <Typography>وقت الفتح: {tx.invoice.order.open_time}</Typography>
                {Array.isArray(tx.invoice.order.items) && tx.invoice.order.items.length > 0 && (
                  <>
                    <Typography sx={{ mt: 1 }}>الأصناف:</Typography>
                    {tx.invoice.order.items.map((item) => (
                      <Box key={item.id} sx={{ pl: 2, mb: 1 }}>
                        <Typography>- {item.product?.name || "-"} x {item.quantity} @ {item.price} = {item.total}</Typography>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        <Box component="section" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">ملاحظات وبيانات إضافية</Typography>
          <Typography>عنوان: {tx.title || "-"}</Typography>
          <Typography>ملاحظة: {tx.note || "-"}</Typography>
          <Typography>سحب - عنوان: {tx.withdrawal_title || "-"}</Typography>
          <Typography>وصف السحب: {tx.withdrawal_description || "-"}</Typography>
          <Typography>إيداع - عنوان: {tx.deposit_title || "-"}</Typography>
          <Typography>وصف الإيداع: {tx.deposit_description || "-"}</Typography>
        </Box>

        <Box component="section" sx={{ mb: 2 }}>
          <Typography variant="subtitle2"> اليومية</Typography>
          <Typography>تاريخ اليومية: {tx.daily_session?.session_date || "-"}</Typography>
        </Box>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">إغلاق</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShiftTransactionsDialog;
